import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { CourseFormFields } from '@/components/ped-content/CourseForm';
import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useInfiniteService from '@/data/hook/useInfiniteService';
import {
  createCoursePath,
  deleteCoursePath,
  ListCoursePathFilters,
  updateCoursePath,
} from '@/data/services/coursePathServices';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import CoursePath from '@/models/CoursePath';
import alert from '@/utils/shared/alert';
import { mountKey } from '@/utils/courses';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ROUTES } from '@/utils/routes';
import { useListCache } from '../query/v2/useCacheManager';
import coursePathVersionsFilters from '@/utils/course-path/coursePathQueryKeysFilters';

type UsePedagogicalContentsProps = {
  moduleId: number;
  courseLineId: number;
  filters?: ListCoursePathFilters;
};

const usePedagogicalContents = ({
  moduleId,
  courseLineId,
  filters,
}: UsePedagogicalContentsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.pedContentTab',
  });
  const { tree } = useTreeCourses();
  const { navigate } = useCourseTreeNavigate();
  const queryClient = useQueryClient();

  const listCacheManager = useListCache(
    coursePathsQueryKeys.list({
      courseLineId: [courseLineId],
      pageSize: ORDENABLE_PAGE_SIZE,
    }).queryKey,
  );

  const {
    results: coursePaths,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
  } = useInfiniteService({
    ...coursePathsQueryKeys.list({
      ...filters,
      moduleId: [moduleId],
      uniqueCourse: true,
    })._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: ({ lessonsQuantity, ...course }: CourseFormFields) =>
      createCoursePath({
        lessonsQuantity,
        course: { ...course, moduleId },
      }),
    onSuccess: coursePath => {
      cacheManager.add(coursePath);
      listCacheManager.add(coursePath);
      const parentKey = mountKey({ type: 'module', id: moduleId });
      tree?.current.insert(
        {
          id: mountKey({ type: 'course', id: coursePath.slug }),
          name: coursePath.course.name,
          type: 'course',
          realId: coursePath.slug,
        },
        parentKey,
      );
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => deleteCoursePath(slug),
    onSuccess: (_, slug) => {
      const target = coursePaths.find(cp => cp.slug === slug);
      if (target) {
        cacheManager.remove(target.id);
        listCacheManager.remove(target.id);
      }
      tree?.current.delete(mountKey({ type: 'course', id: slug }));
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const replaceCoursePath = (
    previousCoursePath: CoursePath,
    newCoursePath: CoursePath,
  ) => {
    cacheManager.remove(previousCoursePath.id);
    cacheManager.add(newCoursePath);
    listCacheManager.remove(previousCoursePath.id);
    listCacheManager.add(newCoursePath);
    queryClient.invalidateQueries({
      queryKey: coursePathsQueryKeys.list({
        ...coursePathVersionsFilters({ courseId: newCoursePath.course.id }),
      }).queryKey,
    });
    const newKey = mountKey({ type: 'course', id: newCoursePath.slug });
    tree?.current.delete(
      mountKey({ type: 'course', id: previousCoursePath.slug }),
    );
    tree?.current.insert(
      {
        id: newKey,
        name: newCoursePath.course.name,
        type: 'course',
        realId: newCoursePath.slug,
      },
      mountKey({ type: 'module', id: moduleId }),
    );
    return newKey;
  };

  const versionMutation = useMutation({
    mutationFn: (coursePath: CoursePath) =>
      updateCoursePath(coursePath.slug, {
        status: VersioningStatusEnum.VERSIONING,
      }),
    onSuccess: (newCoursePath, previousCoursePath) => {
      if (newCoursePath.slug === previousCoursePath.slug) {
        cacheManager.update(newCoursePath);
        listCacheManager.update(newCoursePath);
        alert.success(t('backToVersioningSuccess'));
        return;
      }
      const newKey = replaceCoursePath(previousCoursePath, newCoursePath);
      alert.success(t('versionSuccess'));
      navigate(
        ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(
          courseLineId,
          moduleId,
          newCoursePath.slug,
        ),
        { treeKey: newKey },
      );
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    coursePaths,
    isLoading: isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
    createCoursePath: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    deleteCoursePath: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    createVersion: versionMutation.mutate,
  };
};

export default usePedagogicalContents;
